import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {WebcamImage} from 'ngx-webcam';
import {finalize} from 'rxjs';
import {PropostaExternaEnum} from 'src/app/api/module/erp/shared/enumerable/poposta-externa.enum';
import {DocumentoUploadHelper} from 'src/app/api/module/erp/shared/helper/documento-upload-helper';
import {CompartilharDadosEntreComponentsService} from 'src/app/api/module/erp/shared/services/compartilhar-dados-entre-components.service';
import {SpinnerService} from 'src/app/api/module/erp/shared/services/spinner-service.service';
import {FotoValidacaoService} from 'src/app/api/module/externo/services/photo-validaca/foto.validacao.service';
import {BeneficiarioPropostaSerice} from 'src/app/api/module/externo/services/proposta/beneficiario.proposta.service';
import {ValidacaoDocumentoWebcamComponent} from 'src/app/components/shared/components/validacao-documento-webcam/validacao-documento-webcam.component';

// Interface para representar uma pessoa (titular ou dependente)
export interface PessoaValidacaoFoto {
  id: string;
  titularId?: string;
  dependenteId?: string;
  nome: string;
  tipo: 'titular' | 'dependente';
  parentesco?: string;
  statusValidacao: 'pendente' | 'enviando' | 'successo' | 'error';
  fotoEnviada?: string;
  mensagemErro?: string;
  acordeaoAberto: boolean;
  destacado?: boolean;
  medSeniorCreated?: boolean;
}

@Component({
  selector: 'app-proposta-entidade-validacao-ds-foto',
  templateUrl: './proposta-entidade-validacao-ds-foto.component.html',
  styleUrls: ['./proposta-entidade-validacao-ds-foto.component.scss'],
})
export class PropostaEntidadeValidacaoDsFotoComponent implements OnInit, OnDestroy {
  public listaPessoas: PessoaValidacaoFoto[] = [];
  dadosRecebidosDoBeneciarioVM: any;
  stepAtual: any;
  ehInclusaoDependente: boolean;
  proposalId: any;
  mostrarResumoGeral: boolean = false;

  @Output()
  public respostaEnvioImagem = new EventEmitter<{step: any; mensagem?: string; pessoaId?: string}>();

  @ViewChildren('webcamComponent')
  public webcamComponents!: QueryList<ValidacaoDocumentoWebcamComponent>;

  constructor(
    private _fotoValidacaoService: FotoValidacaoService,
    private cdr: ChangeDetectorRef,
    private _spinnerService: SpinnerService,
    private _documentoUploadHelper: DocumentoUploadHelper,
    private toastr: ToastrService,
    private _compartilharDadosEntreComponentsService: CompartilharDadosEntreComponentsService,
    private ngZone: NgZone,
    private _beneficiarioProposaService: BeneficiarioPropostaSerice,
  ) {}

  // Getter para verificar se todos estão com sucesso
  get todosComSucesso(): boolean {
    if (!this.listaPessoas || this.listaPessoas.length === 0) {
      return false;
    }
    return this.listaPessoas.every(p => p.statusValidacao === 'successo');
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {
    // Pequeno delay para dar tempo ao componente pai de definir os dados no serviço
    setTimeout(() => {
      this.ngZone.run(() => {
        this.receberDadosDoBeneficiario();
        this.obterStatusMedSenior();
      });
    }, 500);
  }

  preencherListaPessoas(): void {
    this.listaPessoas = [];

    // Função auxiliar para gerar UUID
    const gerarUUID = (): string => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };

    // Adicionar titular se existir
    if (this.dadosRecebidosDoBeneciarioVM?.holderProposal) {
      const titular = this.dadosRecebidosDoBeneciarioVM.holderProposal;
      this.listaPessoas.push({
        id: titular.id || gerarUUID(),
        titularId: titular.id,
        nome: titular.name || '',
        tipo: 'titular',
        statusValidacao: 'pendente',
        acordeaoAberto: false,
      });
    }

    // Adicionar dependentes se existirem
    const dependentes =
      this.dadosRecebidosDoBeneciarioVM?.holderProposal?.dependentsProposal ||
      this.dadosRecebidosDoBeneciarioVM?.holderProposal?.dependentProposals;

    if (dependentes && Array.isArray(dependentes)) {
      dependentes.forEach((dependente: any) => {
        this.listaPessoas.push({
          id: dependente.id || gerarUUID(),
          dependenteId: dependente.id,
          nome: dependente.name || '',
          tipo: 'dependente',
          parentesco: dependente.branchCompanyContractKinshipDescription || '',
          statusValidacao: 'pendente',
          acordeaoAberto: false,
        });
      });
    }
  }

  async receberDadosDoBeneficiario(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._compartilharDadosEntreComponentsService.obterDadosCompartilhados().subscribe(
        async data => {
          try {
            if (!data || !Array.isArray(data) || data.length === 0 || !data[0]) {
              this.dadosRecebidosDoBeneciarioVM = null;
              resolve(null);
              return;
            }
            this.dadosRecebidosDoBeneciarioVM = data[0]?.dadosBeneficiario;
            const dadosBeneficiario = this.dadosRecebidosDoBeneciarioVM;
            this.stepAtual = dadosBeneficiario?.step;

            this.ehInclusaoDependente = dadosBeneficiario.dependentInclusion ? true : false;

            // Verifica se existem dados do beneficiário antes de continuar
            if (!dadosBeneficiario) {
              resolve(null);
              return;
            }
            // Atualiza o ID da proposta primeiro
            this.proposalId = dadosBeneficiario.id || '';

            // Preencher a lista de pessoas aqui, dentro do subscribe
            this.preencherListaPessoas();

            // Inicializar acordeões
            if (this.listaPessoas.length > 0) {
              this.listaPessoas.forEach((pessoa, index) => {
                pessoa.acordeaoAberto = false;
              });
              this.cdr.detectChanges();
            }

            resolve(true);
          } catch (error) {
            reject(error);
          }
        },
        error => {
          reject(error);
        },
      );
    });
  }

  obterStatusMedSenior(): void {
    if (!this.proposalId) {
      return;
    }
    this._fotoValidacaoService.obterStatusMedSenior(this.proposalId).subscribe({
      next: response => {
        // Atualizar status do titular
        if (response.holderProposal) {
          const titular = this.listaPessoas.find(p => p.titularId === response.holderProposal.id);
          if (titular && response.holderProposal.medSeniorCreated) {
            titular.statusValidacao = 'successo';
            titular.medSeniorCreated = true;
          }
        }

        // Atualizar status dos dependentes
        if (response.dependentsProposal && Array.isArray(response.dependentsProposal)) {
          response.dependentsProposal.forEach((dep: any) => {
            const dependente = this.listaPessoas.find(p => p.dependenteId === dep.id);
            if (dependente && dep.medSeniorCreated) {
              dependente.statusValidacao = 'successo';

              dependente.medSeniorCreated = true;
            }
          });
        }

        // Se todos já estiverem com sucesso e o step for 13 (RETORNO_DS), mostrar resumo geral
        if (this.todosComSucesso && this.stepAtual === PropostaExternaEnum.RETORNO_DS) {
          this.mostrarResumoGeral = true;
        }

        this.cdr.detectChanges();
      },
      error: err => {
        console.error('Erro ao obter status MedSenior:', err);
      },
    });
  }
  // Alternar acordeão de uma pessoa
  alternarAcordeao(pessoaId: string): void {
    const pessoa = this.listaPessoas.find(p => p.id === pessoaId);
    if (pessoa) {
      // Se o acordeão estiver fechado, abre e fecha todos os outros
      if (!pessoa.acordeaoAberto) {
        this.listaPessoas.forEach(p => {
          p.acordeaoAberto = p.id === pessoaId;
        });
      } else {
        // Se já estiver aberto, fecha
        pessoa.acordeaoAberto = false;
      }
      this.cdr.detectChanges();
    }
  }

  // Evento disparado quando a foto é capturada (após clicar em capturar)
  onFotoCapturada(imagem: WebcamImage, pessoaId: string): void {
    const pessoa = this.listaPessoas.find(p => p.id === pessoaId);
    if (pessoa) {
      this.toastr.info(`Foto capturada para ${pessoa.nome}`);
    }
  }

  // Evento disparado quando o usuário cancela
  onCancelamento(pessoaId: string): void {
    const pessoa = this.listaPessoas.find(p => p.id === pessoaId);
    if (pessoa) {
      console.log(`Usuário cancelou a captura para ${pessoa.nome}`);
    }
  }

  cadastrarVida(imagemBase64: string, pessoaId: string): void {
    const pessoa = this.listaPessoas.find(p => p.id === pessoaId);
    if (!pessoa) return;

    pessoa.statusValidacao = 'enviando';
    this.cdr.detectChanges();

    const arquivoImagem = this._documentoUploadHelper.dataURLtoFile(
      imagemBase64,
      `foto-validacao-${pessoa.id}.jpg`,
      'jpeg',
    );
    const idProposta = this.proposalId;
    const idBeneficiario = pessoa.id;
    const ehDependente = pessoa.tipo === 'titular' ? false : true;
    this._fotoValidacaoService
      .cadastrarVida(arquivoImagem, idProposta, idBeneficiario, ehDependente)
      .pipe(
        finalize(() => {
          this.pararSpinner();
        }),
      )
      .subscribe({
        next: (v: any) => {
          if (v.success) {
            this.toastr.success(`Foto de ${pessoa.nome} enviada para validação.`);
            pessoa.statusValidacao = 'successo';
            pessoa.medSeniorCreated = true;
            pessoa.fotoEnviada = imagemBase64;
            pessoa.acordeaoAberto = false;
            this.mudarStepComponente(pessoaId, 'successo');
            const indiceAtual = this.listaPessoas.findIndex(p => p.id === pessoaId);
            // Encontrar e abrir o próximo item pendente
            this.abrirProximoPendente(indiceAtual);
            this.obterStatusMedSenior();
          } else {
            this.toastr.error(`Face não detectada na foto de ${pessoa.nome}.`);
            pessoa.statusValidacao = 'error';
            pessoa.mensagemErro = 'Face não detectada na foto.';
            this.mudarStepComponente(pessoaId, 'error');
          }
          this.cdr.detectChanges();
        },
        error: (e: any) => {
          this.toastr.toastrConfig.preventDuplicates = true;
          const mensagemTratada = e.message.replace('Error:', '');
          pessoa.statusValidacao = 'error';
          pessoa.mensagemErro = mensagemTratada;
          this.mudarStepComponente(pessoaId, 'error');

          if (mensagemTratada.includes('[object Object]')) {
            this.toastr.error('Erro ao enviar a imagem para validação.');
            this.respostaEnvioImagem.emit({
              step: 'erro',
              mensagem: 'Erro ao enviar a imagem para validação.',
              pessoaId: pessoaId,
            });
          } else {
            this.toastr.error(mensagemTratada);
            this.respostaEnvioImagem.emit({
              step: 'erro',
              mensagem: mensagemTratada,
              pessoaId: pessoaId,
            });
          }
          this.cdr.detectChanges();
        },
      });
  }

  // Abrir o próximo item pendente na lista
  abrirProximoPendente(indiceAtual: number): void {
    this.iniciarSpinner();
    if (indiceAtual === -1) return;
    // Procurar o próximo item pendente a partir do índice atual
    for (let i = indiceAtual + 1; i < this.listaPessoas.length; i++) {
      if (this.listaPessoas[i].statusValidacao === 'pendente') {
        // Adicionar delay para dar feedback visual ao usuário
        setTimeout(() => {
          this.listaPessoas[i].acordeaoAberto = true;
          this.listaPessoas[i].destacado = true;
          this.toastr.info(`Agora validando foto de: ${this.listaPessoas[i].nome}`);
          this.cdr.detectChanges();

          // Scroll suave para o início do acordeão no mobile
          this.scrollToAcordeao(i);

          setTimeout(() => {
            this.listaPessoas[i].destacado = false;
            this.cdr.detectChanges();
            this.pararSpinner();
          }, 2000);
        }, 500);
        return;
      }
    }

    // Se não encontrar depois, procurar desde o início
    for (let i = 0; i < indiceAtual; i++) {
      if (this.listaPessoas[i].statusValidacao === 'pendente') {
        setTimeout(() => {
          this.listaPessoas[i].acordeaoAberto = true;
          this.listaPessoas[i].destacado = true;
          this.toastr.info(`Agora validando foto de: ${this.listaPessoas[i].nome}`);
          this.cdr.detectChanges();

          // Scroll suave para o início do acordeão no mobile
          this.scrollToAcordeao(i);

          setTimeout(() => {
            this.listaPessoas[i].destacado = false;
            this.cdr.detectChanges();
          }, 2000);
        }, 500);
        return;
      }
    }
  }

  // Scroll suave para o acordeão específico
  scrollToAcordeao(indice: number): void {
    setTimeout(() => {
      // Buscar todos os cards de acordeão
      const cards = document.querySelectorAll('.round.mb-2');
      if (cards && cards[indice]) {
        // Verificar se é mobile
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          cards[indice].scrollIntoView({behavior: 'smooth', block: 'start'});
        }
      }
    }, 100);
  }

  // Método auxiliar para mudar o step do componente de webcam de uma pessoa específica
  mudarStepComponente(pessoaId: string, step: string): void {
    const index = this.listaPessoas.findIndex(p => p.id === pessoaId);
    if (index !== -1 && this.webcamComponents) {
      const componente = this.webcamComponents.toArray()[index];
      if (componente) {
        componente.mudarStep(step);
      }
    }
  }

  iniciarSpinner() {
    this._spinnerService.show();
  }

  pararSpinner(ms?: number) {
    return new Promise(resolve => {
      setTimeout(() => {
        this._spinnerService.hide();
        resolve(true);
      }, ms);
    });
  }
  // Método para avançar para o próximo item pendente manualmente
  avancarProximoStep() {
    if (this.todosComSucesso) {
      this.stepAtual = PropostaExternaEnum.RETORNO_DS;
      this.iniciarSpinner();
      this._beneficiarioProposaService
        .atualizarStep(this.proposalId, this.stepAtual)
        .pipe(finalize(() => this.pararSpinner()))
        .subscribe({
          next: () => {
            this.toastr.success('Proposta avançada para Documento análise com sucesso!');
            this.toastr.toastrConfig.preventDuplicates = true;
            this.mostrarResumoGeral = true;
            this.cdr.detectChanges();
          },
          error: err => {
            this.toastr.error('Erro ao avançar proposta. Tente novamente.');
            console.error('Erro ao atualizar step:', err);
          },
        });
    } else {
      // Verificar quantas fotos pendentes
      const pendentes = this.listaPessoas.filter(p => p.statusValidacao !== 'successo').length;
      this.toastr.warning(`Ainda há ${pendentes} foto(s) pendente(s). Envie todas as fotos para continuar.`);
    }
  }
}
